import React, { useRef, useState, useEffect } from 'react';
import './InlineCamera.css';

const InlineCamera = ({ side, onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraStarted, setCameraStarted] = useState(false);

  // Cleanup function
  const stopCamera = () => {
    // Clear video element first
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.onloadedmetadata = null;
      videoRef.current.srcObject = null;
    }
    // Then stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      streamRef.current = null;
    }
    setCameraStarted(false);
  };

  // Auto-start camera when component mounts
  useEffect(() => {
    let isMounted = true;
    
    const startCamera = async () => {
      setLoading(true);
      
      // Stop any existing stream first
      stopCamera();
      
      try {
        let constraints = {
          video: {
            facingMode: { exact: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        };

        let mediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
          console.log('Exact facingMode failed, trying ideal...');
          constraints.video.facingMode = { ideal: "environment" };
          mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        }

        if (!isMounted) {
          mediaStream.getTracks().forEach(track => track.stop());
          return;
        }

        streamRef.current = mediaStream;
        
        if (videoRef.current) {
          // Clear any existing srcObject first
          videoRef.current.srcObject = null;
          
          // Small delay before setting new stream
          await new Promise(resolve => setTimeout(resolve, 100));
          
          if (!isMounted || !videoRef.current) return;
          
          videoRef.current.srcObject = mediaStream;
          
          // Wait for video to be ready before playing
          videoRef.current.onloadedmetadata = async () => {
            if (!videoRef.current || !isMounted) return;
            
            try {
              // Pause first to ensure clean state
              videoRef.current.pause();
              await videoRef.current.play();
              
              if (isMounted) {
                setLoading(false);
                setCameraStarted(true);
                setError(null);
              }
            } catch (err) {
              // AbortError is expected when component unmounts during play
              if (err.name !== 'AbortError') {
                console.warn('Play error:', err.message);
              }
              if (isMounted) {
                setLoading(false);
                setCameraStarted(true);
              }
            }
          };
        }
      } catch (err) {
        console.error('Camera error:', err);
        if (isMounted) {
          setError('Unable to access camera. Please check permissions or upload a file instead.');
          setLoading(false);
        }
      }
    };

    startCamera();
    
    return () => {
      isMounted = false;
      stopCamera();
    };
  }, []);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Convert to base64 with high quality
    const base64String = canvas.toDataURL('image/jpeg', 0.95).split(',')[1];
    
    stopCamera();
    onCapture(base64String);
  };

  const handleCapture = () => {
    let count = 3;
    setCountdown(count);
    
    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(interval);
        setCountdown(null);
        capturePhoto();
      }
    }, 1000);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target.result.split(',')[1];
      stopCamera();
      onCapture(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <div className="camera-overlay">
      <div className="camera-modal">
        <div className="camera-header">
          <h3>Capture {side === 'front' ? 'Front' : 'Back'} of ID</h3>
          <button 
            onClick={handleClose}
            className="close-button"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="camera-error-banner">
            <p>{error}</p>
          </div>
        )}

        <div className="camera-content">
          {/* Camera View */}
          {!error && (
            <div className="camera-view-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
              />
              
              {loading && (
                <div className="camera-loading-overlay">
                  <div className="spinner-camera"></div>
                  <p>Starting camera...</p>
                </div>
              )}
              
              {countdown && (
                <div className="countdown-overlay">
                  <div className="countdown-number">{countdown}</div>
                </div>
              )}
              
              {cameraStarted && !loading && (
                <div className="camera-guide-frame">
                  <div className="guide-corner guide-tl"></div>
                  <div className="guide-corner guide-tr"></div>
                  <div className="guide-corner guide-bl"></div>
                  <div className="guide-corner guide-br"></div>
                  <div className="guide-text">Align ID within frame</div>
                </div>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="camera-controls">
            {cameraStarted && !error && (
              <button 
                onClick={handleCapture}
                disabled={countdown !== null || loading}
                className="capture-button-large"
              >
                <div className="capture-button-circle">
                  {countdown !== null ? countdown : '📷'}
                </div>
              </button>
            )}

            {/* Upload Option */}
            <div className="upload-option">
              <div className="option-divider">or</div>
              <button 
                onClick={() => fileInputRef.current?.click()} 
                className="upload-file-button"
              >
                📁 Upload File Instead
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <div className="camera-tips">
            <p>📋 Tips for best results:</p>
            <ul>
              <li>Ensure good lighting</li>
              <li>Keep ID flat and within frame</li>
              <li>Avoid shadows and glare</li>
            </ul>
          </div>
        </div>

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

export default InlineCamera;